import { Banner, Button, Modal, Text } from '@innovationdepartment/proxima-ui';
import { ModalActions, ModalContent } from './ConfirmationModal.styles';

/** Props for the reusable confirm/cancel modal. */
type ConfirmationModalProps = {
  /** Whether the modal is visible. */
  open: boolean;
  /** Heading shown in the modal chrome. */
  title: string;
  /** Body copy explaining what the confirmation does. */
  body: string;
  /** Optional amber warning banner rendered below the body. Omit to hide it. */
  warning?: { title: string; body: string };
  /** Label for the confirm button in its resting state. */
  confirmLabel: string;
  /** Label swapped onto the confirm button while `busy` is true. Falls back to `confirmLabel`. */
  busyLabel?: string;
  /** Color of the confirm button. Defaults to `success`. */
  confirmColor?: 'success' | 'error';
  /** When true, the confirm button is disabled and shows `busyLabel`. */
  busy?: boolean;
  /** Fired when the confirm button is clicked. */
  onConfirm: () => void;
  /** Fired when the modal is closed or the cancel button is clicked. */
  onCancel: () => void;
};

/**
 * Presentational confirm/cancel modal with an optional warning banner.
 *
 * Pure presentation — the caller owns the copy, the confirm action, and any
 * busy state. Cancel is wired to both the modal's close affordance and the
 * Cancel button.
 */
export const ConfirmationModal = ({
  open,
  title,
  body,
  warning,
  confirmLabel,
  busyLabel,
  confirmColor = 'success',
  busy = false,
  onConfirm,
  onCancel
}: ConfirmationModalProps) => (
  <Modal open={open} title={title} onClose={onCancel}>
    <ModalContent>
      <Text variant="body1">{body}</Text>
      {warning ? <Banner type="warning" title={warning.title} body={warning.body} /> : null}
      <ModalActions>
        <Button label="Cancel" variant="outlined" size="small" onClick={onCancel} />
        <Button
          label={busy ? (busyLabel ?? confirmLabel) : confirmLabel}
          color={confirmColor}
          size="small"
          onClick={onConfirm}
          disabled={busy}
        />
      </ModalActions>
    </ModalContent>
  </Modal>
);
